// src/components/admin/HistorialFichasAdmin.js

import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
  FaHistory,
  FaCalendarAlt,
  FaClock,
  FaChevronDown,
  FaChevronUp,
  FaArrowRight
} from 'react-icons/fa';

const APP_PRIMARY = '#b03060';

/* ===== Traducciones de campos ===== */
const LABELS = {
  peso: 'Peso corporal',
  edad: 'Edad',
  altura: 'Estatura / Altura',
  objetivoEntrenamiento: 'Objetivo de entrenamiento',
  actividadOcupacion: 'Actividad u Ocupación',
  experienciaPesas: 'Experiencia en pesas',
  circunferenciaCintura: 'Circunferencia cintura',
  horasSueno: 'Horas de sueño',
  fuma: 'Fuma',
  bebeAlcohol: 'Bebe alcohol',
  practicaDeporte: 'Practica deporte',
  frecuenciaDeporte: 'Frecuencia deporte',
  nutricionista: 'Nutricionista',
  interesNutricion: 'Interés en nutrición',
  motivo: 'Motivo de entrenamiento'
};

/* ===== Formateador fecha Chile ===== */
const formatearFecha = (timestamp) => {
  if (!timestamp) return null;

  const d = timestamp.toDate();

  return {
    dia: d.toLocaleDateString('es-CL', {
      timeZone: 'America/Santiago',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }),
    hora: d.toLocaleTimeString('es-CL', {
      timeZone: 'America/Santiago',
      hour: '2-digit',
      minute: '2-digit'
    }),
    raw: d
  };
};

const MAX_HISTORIALES = 8;
const DIAS_MAX = 30;

const HistorialFichasAdmin = ({ uid }) => {
  const [historialPorDia, setHistorialPorDia] = useState({});
  const [abierto, setAbierto] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const cargarHistorial = async () => {
      if (!uid) {
        setCargando(false);
        return;
      }

      try {
        const ref = collection(db, 'usuarios', uid, 'historialFichas');

        const q = query(
          ref,
          orderBy('creadaEn', 'desc'),
          limit(MAX_HISTORIALES)
        );

        const snap = await getDocs(q);
        const hoy = new Date();
        const agrupado = {};

        snap.docs.forEach((doc) => {
          const data = doc.data();
          const fecha = formatearFecha(data.creadaEn);
          if (!fecha) return;

          const diffDias = (hoy - fecha.raw) / (1000 * 60 * 60 * 24);
          if (diffDias > DIAS_MAX) return;

          if (!agrupado[fecha.dia]) {
            agrupado[fecha.dia] = [];
          }

          agrupado[fecha.dia].push({
            id: doc.id,
            hora: fecha.hora,
            cambios: data.cambios || {}
          });
        });

        setHistorialPorDia(agrupado);
      } catch (e) {
        console.error('Error cargando historial:', e);
      } finally {
        setCargando(false);
      }
    };

    cargarHistorial();
  }, [uid]);

  if (cargando) {
    return (
      <div style={styles.card}>
        <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>
          Cargando historial de cambios...
        </p>
      </div>
    );
  }

  const tieneRegistros = Object.keys(historialPorDia).length > 0;

  return (
    <div style={styles.card}>
      {/* Cabecera de la tarjeta */}
      <div style={styles.header}>
        <div style={styles.iconBox}>
          <FaHistory color={APP_PRIMARY} size={16} />
        </div>
        <div>
          <h2 style={styles.title}>Historial de Modificaciones</h2>
          <p style={styles.subtitle}>
            Registro cronológico de cambios realizados por el alumno en su ficha
          </p>
        </div>
      </div>

      {!tieneRegistros ? (
        <div style={styles.emptyBox}>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: 14 }}>
            No hay modificaciones registradas en los últimos 30 días.
          </p>
        </div>
      ) : (
        <div style={styles.timeline}>
          {Object.entries(historialPorDia).map(([dia, cambiosDia], indexDia) => (
            <div key={dia} style={styles.dayGroup}>
              {/* Etiqueta de la fecha */}
              <div style={styles.dayBadge}>
                <FaCalendarAlt size={12} color={APP_PRIMARY} />
                <span>{dia}</span>
              </div>

              {/* Lista de cambios por cada hora del día */}
              <div style={styles.itemsList}>
                {cambiosDia.map((h, i) => {
                  const key = `${indexDia}-${i}`;
                  const isOpen = abierto === key;
                  const totalCambios = Object.keys(h.cambios).length;

                  return (
                    <div key={h.id} style={styles.recordItem}>
                      <button
                        type="button"
                        onClick={() => setAbierto(isOpen ? null : key)}
                        style={{
                          ...styles.recordHeader,
                          backgroundColor: isOpen ? '#fdf2f8' : '#ffffff'
                        }}
                      >
                        <div style={styles.recordTime}>
                          <FaClock size={12} color={isOpen ? APP_PRIMARY : '#64748b'} />
                          <span style={{ fontWeight: '600', color: isOpen ? APP_PRIMARY : '#1e293b' }}>
                            {h.hora} hrs
                          </span>
                          <span style={styles.changesCount}>
                            {totalCambios === 1 ? '1 modificación' : `${totalCambios} modificaciones`}
                          </span>
                        </div>
                        <div style={{ color: isOpen ? APP_PRIMARY : '#94a3b8' }}>
                          {isOpen ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
                        </div>
                      </button>

                      {isOpen && (
                        <div style={styles.recordBody}>
                          {totalCambios === 0 ? (
                            <p style={{ margin: 0, color: '#94a3b8', fontSize: 13 }}>
                              No se registraron cambios específicos.
                            </p>
                          ) : (
                            <div style={styles.diffGrid}>
                              {Object.entries(h.cambios).map(([campo, valores]) => (
                                <div key={campo} style={styles.diffRow}>
                                  <div style={styles.diffField}>
                                    {LABELS[campo] || campo}
                                  </div>
                                  <div style={styles.diffValues}>
                                    <span style={styles.valBefore}>
                                      {valores.antes || '—'}
                                    </span>
                                    <FaArrowRight size={10} color="#94a3b8" />
                                    <span style={styles.valAfter}>
                                      {valores.despues || '—'}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HistorialFichasAdmin;

/* =========================== ESTILOS =========================== */
const styles = {
  card: {
    background: '#ffffff',
    borderRadius: 20,
    padding: '24px',
    border: '1px solid #f1f5f9',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
    fontFamily: "'Poppins', sans-serif"
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
    borderBottom: '1px solid #f1f5f9',
    paddingBottom: 14
  },
  iconBox: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: '#fdf2f8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  subtitle: {
    fontSize: 12,
    color: '#64748b',
    margin: '2px 0 0 0'
  },
  emptyBox: {
    padding: '24px',
    textAlign: 'center',
    background: '#f8fafc',
    borderRadius: 12,
    border: '1px dashed #e2e8f0'
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  },
  dayGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  dayBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    alignSelf: 'flex-start',
    background: '#fdf2f8',
    color: APP_PRIMARY,
    border: '1px solid #fbcfe8',
    padding: '4px 12px',
    borderRadius: 50,
    fontSize: 12,
    fontWeight: '700'
  },
  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    paddingLeft: 4
  },
  recordItem: {
    border: '1px solid #f1f5f9',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
  },
  recordHeader: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
    fontFamily: 'inherit'
  },
  recordTime: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 13
  },
  changesCount: {
    fontSize: 11,
    background: '#f1f5f9',
    color: '#64748b',
    padding: '2px 8px',
    borderRadius: 20,
    fontWeight: '500'
  },
  recordBody: {
    padding: '14px 16px',
    background: '#ffffff',
    borderTop: '1px solid #f1f5f9'
  },
  diffGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  diffRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    padding: '8px 12px',
    background: '#f8fafc',
    borderRadius: 8,
    border: '1px solid #f1f5f9'
  },
  diffField: {
    fontSize: 12,
    fontWeight: '600',
    color: '#334155'
  },
  diffValues: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12
  },
  valBefore: {
    color: '#94a3b8',
    textDecoration: 'line-through',
    background: '#ffffff',
    padding: '2px 6px',
    borderRadius: 4,
    border: '1px solid #e2e8f0'
  },
  valAfter: {
    color: '#15803d',
    fontWeight: '600',
    background: '#f0fdf4',
    padding: '2px 6px',
    borderRadius: 4,
    border: '1px solid #bbf7d0'
  }
};


